import { useEffect, useMemo, useRef, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import {
  type PaymentTerminal,
  PaymentTerminalsDocument,
  type PaymentTerminalsQuery,
  type PaymentTerminalsQueryVariables,
} from '~/api/graphql/generated/types'

const MAX_CONSECUTIVE_ERRORS = 3
const EMPTY_ARRAY: never[] = []

function getPaymentTerminalOptions(terminals: PaymentTerminal[]) {
  return terminals
    .map((terminal) => ({
      value: terminal.externalDeviceId,
      label:
        terminal.state.name === 'Available'
          ? terminal.displayName
          : `${terminal.displayName} (${terminal.state.name})`,
      selectable: terminal.state.selectable,
    }))
    .sort((a, b) => {
      if (a.selectable && !b.selectable) return -1
      if (!a.selectable && b.selectable) return 1
      return a.label.localeCompare(b.label)
    })
}

export function usePaymentTerminals() {
  const failuresRef = useRef(0)
  const [isFinishedLoading, setIsFinishedLoading] = useState(false)
  const { data, error, networkStatus, stopPolling, refetch } = useQuery<
    PaymentTerminalsQuery,
    PaymentTerminalsQueryVariables
  >(PaymentTerminalsDocument, {
    fetchPolicy: 'network-only',
    pollInterval: 3000,
    notifyOnNetworkStatusChange: true,
    returnPartialData: false,
  })

  const terminals = data?.paymentTerminals ?? EMPTY_ARRAY
  const hasSelectableTerminals = terminals.some((t) => t.state.selectable)

  const isSuccessTick =
    networkStatus === NetworkStatus.ready && !error && hasSelectableTerminals

  useEffect(() => {
    // React only when a poll cycle finishes (ready or error)
    if (
      networkStatus !== NetworkStatus.ready &&
      networkStatus !== NetworkStatus.error
    )
      return

    if (isSuccessTick) {
      failuresRef.current = 0
      stopPolling()
      setIsFinishedLoading(true)
    } else {
      failuresRef.current += 1
      if (failuresRef.current >= MAX_CONSECUTIVE_ERRORS) {
        stopPolling()
        setIsFinishedLoading(true)
      }
    }
  }, [networkStatus, isSuccessTick, stopPolling])

  const safeTerminalsToShow = isSuccessTick ? terminals : EMPTY_ARRAY
  const paymentTerminalOptions = useMemo(
    () => getPaymentTerminalOptions(safeTerminalsToShow as PaymentTerminal[]),
    [safeTerminalsToShow],
  )

  return {
    paymentTerminalOptions,
    loading: !isFinishedLoading,
    error,
    refetch,
  }
}
